import { useState, useEffect } from "react";

const API_BASE = "http://localhost:8000";

const REGULATORY_FRAMEWORKS = [
  {
    id: "sox",
    label: "SOX",
    full: "Sarbanes-Oxley Act",
    desc: "Financial reporting controls",
    color: "#4f8ef7",
  },
  {
    id: "glba",
    label: "GLBA",
    full: "Gramm-Leach-Bliley Act",
    desc: "Customer financial data privacy",
    color: "#a78bfa",
  },
  {
    id: "pci_dss",
    label: "PCI-DSS",
    full: "Payment Card Industry DSS",
    desc: "Cardholder data protection",
    color: "#34d399",
  },
  {
    id: "ffiec",
    label: "FFIEC",
    full: "Federal Financial Institutions Examination Council",
    desc: "Banking cybersecurity guidance",
    color: "#f59e0b",
  },
  {
    id: "nist",
    label: "NIST CSF",
    full: "NIST Cybersecurity Framework",
    desc: "Risk management standards",
    color: "#f87171",
  },
];

const CATEGORY_LABELS = {
  identity_access: "Identity & Access Management",
  network_security: "Network Security",
  endpoint_protection: "Endpoint Protection",
  data_protection: "Data Protection",
  incident_response: "Incident Response",
  vulnerability_management: "Vulnerability Management",
  cloud_security: "Cloud Security",
  application_security: "Application Security",
  third_party_risk: "Third-Party Risk",
  compliance_governance: "Compliance & Governance",
  security_awareness: "Security Awareness",
  physical_security: "Physical Security",
  business_continuity: "Business Continuity",
  threat_intelligence: "Threat Intelligence",
  security_operations: "Security Operations",
};

const getRiskLevel = (score) => {
  if (score <= 1) return { label: "Critical", color: "#ef4444" };
  if (score <= 2) return { label: "High", color: "#f97316" };
  if (score <= 3) return { label: "Medium", color: "#eab308" };
  return { label: "Low", color: "#22c55e" };
};

const getGapSeverity = (gap) => {
  if (gap >= 3) return { label: "Large Gap", color: "#ef4444" };
  if (gap >= 2) return { label: "Moderate Gap", color: "#f97316" };
  if (gap >= 1) return { label: "Small Gap", color: "#eab308" };
  return { label: "On Target", color: "#22c55e" };
};

export default function StrategyPaper() {
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [targetDate, setTargetDate] = useState("");
  const [frameworks, setFrameworks] = useState([]);
  const [executiveName, setExecutiveName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [additionalContext, setAdditionalContext] = useState("");
  const [priorityOverrides, setPriorityOverrides] = useState({});

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [generatedPaper, setGeneratedPaper] = useState(null);
  const [editableContent, setEditableContent] = useState("");
  const [versions, setVersions] = useState([]);
  const [activeTab, setActiveTab] = useState("configure"); // configure | review | history

  useEffect(() => {
    fetchAssessment();
  }, []);

  const fetchAssessment = async () => {
    try {
      setLoading(true);
      const customerId =
        JSON.parse(localStorage.getItem("ciso_user") || "{}").customer_id || "";
      const assessmentId = localStorage.getItem("current_assessment_id") || "1";

      const token = localStorage.getItem("ciso_access_token");
      const res = await fetch(
        `${API_BASE}/api/assessments/current?customer_id=${customerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to load assessment");
      const data = await res.json();
      const assessmentData = data.assessment || data;
      setAssessment(assessmentData);

      // Pre-populate org name if available
      if (assessmentData.company_name) setOrganizationName(assessmentData.company_name);

      // Set default target date to 12 months out
      const d = new Date();
      d.setFullYear(d.getFullYear() + 1);
      setTargetDate(d.toISOString().split("T")[0]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleFramework = (id) => {
    setFrameworks((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );
  };

  const togglePriorityOverride = (key) => {
    setPriorityOverrides((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getGaps = () => {
    if (!assessment?.responses) return [];
    return Object.entries(assessment.responses)
      .map(([key, val]) => ({
        key,
        label: CATEGORY_LABELS[key] || key,
        current: val.score || 0,
        target: val.target || 5,
        gap: (val.target || 5) - (val.score || 0),
      }))
      .filter((g) => g.gap > 0)
      .sort((a, b) => b.gap - a.gap);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const customerId = JSON.parse(localStorage.getItem("ciso_user") || "{}").customer_id || "";
      const excludedCategories = Object.entries(priorityOverrides)
        .filter(([, included]) => included === false)
        .map(([key]) => key);

      const res = await fetch(`${API_BASE}/api/strategy/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_id: customerId,
          organization_name: organizationName || "Your Organization",
          executive_name: executiveName,
          target_date: targetDate,
          frameworks,
          additional_context: additionalContext,
          excluded_categories: excludedCategories,
          assessment_responses: assessment?.responses || {},
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Generation failed");
      }

      const data = await res.json();
      const newVersion = {
        id: Date.now(),
        timestamp: new Date().toLocaleString(),
        label: `Version ${versions.length + 1}`,
        content: data.content,
        params: { targetDate, frameworks, executiveName, organizationName },
      };

      setVersions((prev) => [newVersion, ...prev]);
      setGeneratedPaper(data.content);
      setEditableContent(data.content);
      setActiveTab("review");
    } catch (err) {
      setError(`Generation failed: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };
  const handleSaveVersion = () => {
    const newVersion = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      label: `Version ${versions.length + 1} (Edited)`,
      content: editableContent,
      params: { targetDate, frameworks, executiveName, organizationName },
    };
    setVersions((prev) => [newVersion, ...prev]);
    alert("Version saved.");
  };

  const handleRestoreVersion = (v) => {
    setEditableContent(v.content);
    setActiveTab("review");
  };

  const handleDownload = () => {
    const blob = new Blob([editableContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `CISO_Strategy_Paper_${new Date().toISOString().split("T")[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const gaps = getGaps();

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.headerTitle}>Strategy Paper Generator</h1>
          <p style={styles.headerSub}>
            Generate a customized cybersecurity remediation strategy for your
            financial institution
          </p>
        </div>
        <div style={styles.headerRight}>
          {generatedPaper && (
            <button style={styles.downloadBtn} onClick={handleDownload}>
              ⬇ Download
            </button>
          )}
        </div>
      </div>

      {/* Tab Bar */}
      <div style={styles.tabBar}>
        {[
          { id: "configure", label: "① Configure" },
          { id: "review", label: "② Review & Edit", disabled: !generatedPaper },
          { id: "history", label: "③ Version History", disabled: versions.length === 0 },
        ].map((tab) => (
          <button
            key={tab.id}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {}),
              ...(tab.disabled ? styles.tabDisabled : {}),
            }}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* CONFIGURE TAB */}
      {activeTab === "configure" && (
        <div style={styles.content}>
          <div style={styles.twoCol}>
            {/* LEFT: Form */}
            <div style={styles.col}>
              {/* Organization Details */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardIcon}>🏦</span>
                  <h3 style={styles.cardTitle}>Organization Details</h3>
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Organization Name</label>
                  <input
                    style={styles.input}
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder="First National Bank"
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Prepared For</label>
                  <input
                    style={styles.input}
                    value={executiveName}
                    onChange={(e) => setExecutiveName(e.target.value)}
                    placeholder="Chief Information Security Officer"
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Target Completion Date</label>
                  <input
                    style={styles.input}
                    type="date"
                    value={targetDate}
                    onChange={(e) => setTargetDate(e.target.value)}
                  />
                  <span style={styles.fieldHint}>
                    Remediation roadmap will be scoped to this timeline
                  </span>
                </div>
              </div>

              {/* Regulatory Frameworks */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardIcon}>⚖️</span>
                  <h3 style={styles.cardTitle}>Regulatory Frameworks</h3>
                </div>
                <p style={styles.cardDesc}>
                  Select all applicable frameworks. The strategy paper will map
                  recommendations to specific control requirements.
                </p>
                <div style={styles.frameworkGrid}>
                  {REGULATORY_FRAMEWORKS.map((fw) => (
                    <button
                      key={fw.id}
                      style={{
                        ...styles.frameworkBtn,
                        ...(frameworks.includes(fw.id)
                          ? {
                              ...styles.frameworkBtnActive,
                              borderColor: fw.color,
                              boxShadow: `0 0 0 1px ${fw.color}40`,
                            }
                          : {}),
                      }}
                      onClick={() => toggleFramework(fw.id)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div
                          style={{
                            ...styles.frameworkDot,
                            background: frameworks.includes(fw.id)
                              ? fw.color
                              : "#374151",
                          }}
                        />
                        <span style={styles.frameworkLabel}>{fw.label}</span>
                      </div>
                      <span style={styles.frameworkFull}>{fw.full}</span>
                      <span style={styles.frameworkDesc}>{fw.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Context */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardIcon}>📝</span>
                  <h3 style={styles.cardTitle}>Additional Context</h3>
                </div>
                <textarea
                  style={styles.textarea}
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder="Include any specific constraints, upcoming audits, recent incidents, or priorities not captured in the assessment..."
                  rows={4}
                />
              </div>
            </div>

            {/* RIGHT: Assessment Preview */}
            <div style={styles.col}>
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardIcon}>📊</span>
                  <h3 style={styles.cardTitle}>Assessment Data</h3>
                  <span style={styles.autoBadge}>Auto-populated</span>
                </div>

                {loading ? (
                  <div style={styles.loadingRow}>Loading assessment...</div>
                ) : error ? (
                  <div style={styles.errorRow}>⚠ {error}</div>
                ) : gaps.length === 0 ? (
                  <div style={styles.emptyRow}>No gaps found in assessment</div>
                ) : (
                  <>
                    <p style={styles.cardDesc}>
                      {gaps.length} capability gaps identified. Toggle any to
                      exclude from the strategy paper.
                    </p>
                    <div style={styles.gapList}>
                      {gaps.map((g) => {
                        const risk = getRiskLevel(g.current);
                        const gapSev = getGapSeverity(g.gap);
                        const excluded = priorityOverrides[g.key] === false;
                        return (
                          <div
                            key={g.key}
                            style={{
                              ...styles.gapRow,
                              opacity: excluded ? 0.4 : 1,
                            }}
                          >
                            <div style={styles.gapLeft}>
                              <div style={styles.gapLabel}>{g.label}</div>
                              <div style={styles.gapMeta}>
                                <span
                                  style={{
                                    ...styles.badge,
                                    background: risk.color + "20",
                                    color: risk.color,
                                  }}
                                >
                                  {risk.label}
                                </span>
                                <span
                                  style={{
                                    ...styles.badge,
                                    background: gapSev.color + "20",
                                    color: gapSev.color,
                                  }}
                                >
                                  {gapSev.label}
                                </span>
                              </div>
                            </div>
                            <div style={styles.gapRight}>
                              <div style={styles.gapScore}>
                                <span style={styles.gapCurrent}>{g.current}</span>
                                <span style={styles.gapArrow}>→</span>
                                <span style={styles.gapTarget}>{g.target}</span>
                              </div>
                              <button
                                style={styles.toggleBtn}
                                onClick={() =>
                                  setPriorityOverrides((prev) => ({
                                    ...prev,
                                    [g.key]: prev[g.key] === false ? true : false,
                                  }))
                                }
                              >
                                {excluded ? "Include" : "Exclude"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Generate Button */}
              <button
                style={{
                  ...styles.generateBtn,
                  opacity: generating ? 0.7 : 1,
                  cursor: generating ? "not-allowed" : "pointer",
                }}
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating ? (
                  <span>⟳ Generating Strategy Paper...</span>
                ) : (
                  <span>✦ Generate Strategy Paper</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REVIEW TAB */}
      {activeTab === "review" && generatedPaper && (
        <div style={styles.content}>
          <div style={styles.reviewHeader}>
            <p style={styles.reviewHint}>
              Review and edit the generated strategy paper below. Changes are
              saved as a new version.
            </p>
            <div style={styles.reviewActions}>
              <button style={styles.secondaryBtn} onClick={handleSaveVersion}>
                💾 Save Version
              </button>
              <button style={styles.downloadBtn} onClick={handleDownload}>
                ⬇ Download .md
              </button>
            </div>
          </div>
          <textarea
            style={styles.editor}
            value={editableContent}
            onChange={(e) => setEditableContent(e.target.value)}
            spellCheck={false}
          />
        </div>
      )}

      {/* HISTORY TAB */}
      {activeTab === "history" && (
        <div style={styles.content}>
          <div style={styles.historyList}>
            {versions.map((v) => (
              <div key={v.id} style={styles.historyCard}>
                <div style={styles.historyLeft}>
                  <div style={styles.historyLabel}>{v.label}</div>
                  <div style={styles.historyMeta}>{v.timestamp}</div>
                  <div style={styles.historyParams}>
                    {v.params.frameworks
                      .map(
                        (f) =>
                          REGULATORY_FRAMEWORKS.find((r) => r.id === f)?.label
                      )
                      .join(", ") || "No frameworks"}{" "}
                    · Target: {v.params.targetDate}
                  </div>
                </div>
                <button
                  style={styles.restoreBtn}
                  onClick={() => handleRestoreVersion(v)}
                >
                  Restore
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#0d0f17",
    color: "#e2e8f0",
    fontFamily: "'DM Sans', 'Outfit', system-ui, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "32px 40px 24px",
    borderBottom: "1px solid #1e2535",
  },
  headerLeft: { display: "flex", flexDirection: "column", gap: 6 },
  headerTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: "#f1f5f9",
    margin: 0,
    letterSpacing: "-0.02em",
  },
  headerSub: { fontSize: 14, color: "#64748b", margin: 0 },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  tabBar: {
    display: "flex",
    gap: 2,
    padding: "0 40px",
    borderBottom: "1px solid #1e2535",
    background: "#0a0c13",
  },
  tab: {
    padding: "14px 20px",
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    color: "#64748b",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.15s",
  },
  tabActive: {
    color: "#a78bfa",
    borderBottomColor: "#7c3aed",
  },
  tabDisabled: { opacity: 0.3, cursor: "not-allowed" },
  content: { padding: "32px 40px" },
  twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 },
  col: { display: "flex", flexDirection: "column", gap: 20 },
  card: {
    background: "#111827",
    border: "1px solid #1e2535",
    borderRadius: 12,
    padding: 24,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
  },
  cardIcon: { fontSize: 18 },
  cardTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#f1f5f9",
    margin: 0,
    flex: 1,
  },
  cardDesc: { fontSize: 13, color: "#64748b", marginBottom: 16, lineHeight: 1.5 },
  autoBadge: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: "#34d399",
    background: "#34d39918",
    border: "1px solid #34d39930",
    borderRadius: 4,
    padding: "2px 7px",
  },
  fieldGroup: { marginBottom: 16 },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#94a3b8",
    letterSpacing: "0.06em",
    marginBottom: 6,
    textTransform: "uppercase",
  },
  input: {
    width: "100%",
    background: "#0d0f17",
    border: "1px solid #1e2535",
    borderRadius: 8,
    padding: "10px 14px",
    color: "#e2e8f0",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s",
  },
  fieldHint: { fontSize: 11, color: "#475569", marginTop: 4, display: "block" },
  textarea: {
    width: "100%",
    background: "#0d0f17",
    border: "1px solid #1e2535",
    borderRadius: 8,
    padding: "12px 14px",
    color: "#e2e8f0",
    fontSize: 14,
    outline: "none",
    resize: "vertical",
    boxSizing: "border-box",
    fontFamily: "inherit",
    lineHeight: 1.6,
  },
  frameworkGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
  },
  frameworkBtn: {
    background: "#0d0f17",
    border: "1px solid #1e2535",
    borderRadius: 10,
    padding: "12px 14px",
    textAlign: "left",
    cursor: "pointer",
    transition: "all 0.15s",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  frameworkBtnActive: { background: "#13111f" },
  frameworkDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  frameworkLabel: { fontSize: 13, fontWeight: 700, color: "#f1f5f9" },
  frameworkFull: { fontSize: 11, color: "#64748b", lineHeight: 1.3 },
  frameworkDesc: { fontSize: 11, color: "#475569" },
  gapList: { display: "flex", flexDirection: "column", gap: 8 },
  gapRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 12px",
    background: "#0d0f17",
    borderRadius: 8,
    border: "1px solid #1e2535",
    transition: "opacity 0.2s",
  },
  gapLeft: { flex: 1, display: "flex", flexDirection: "column", gap: 5 },
  gapLabel: { fontSize: 13, fontWeight: 500, color: "#e2e8f0" },
  gapMeta: { display: "flex", gap: 6 },
  badge: {
    fontSize: 10,
    fontWeight: 600,
    borderRadius: 4,
    padding: "2px 6px",
    letterSpacing: "0.04em",
  },
  gapRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 6,
  },
  gapScore: { display: "flex", alignItems: "center", gap: 5 },
  gapCurrent: { fontSize: 13, fontWeight: 700, color: "#ef4444" },
  gapArrow: { fontSize: 11, color: "#475569" },
  gapTarget: { fontSize: 13, fontWeight: 700, color: "#22c55e" },
  toggleBtn: {
    fontSize: 11,
    color: "#64748b",
    background: "none",
    border: "1px solid #1e2535",
    borderRadius: 4,
    padding: "2px 8px",
    cursor: "pointer",
  },
  generateBtn: {
    width: "100%",
    padding: "16px",
    background: "linear-gradient(135deg, #6d28d9, #4f46e5)",
    border: "none",
    borderRadius: 12,
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: "0.02em",
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: "0 4px 20px #6d28d940",
  },
  downloadBtn: {
    padding: "9px 18px",
    background: "#1e2535",
    border: "1px solid #2d3748",
    borderRadius: 8,
    color: "#e2e8f0",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryBtn: {
    padding: "9px 18px",
    background: "#1e2535",
    border: "1px solid #2d3748",
    borderRadius: 8,
    color: "#a78bfa",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  reviewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  reviewHint: { fontSize: 13, color: "#64748b", margin: 0 },
  reviewActions: { display: "flex", gap: 10 },
  editor: {
    width: "100%",
    minHeight: 600,
    background: "#111827",
    border: "1px solid #1e2535",
    borderRadius: 12,
    padding: 24,
    color: "#e2e8f0",
    fontSize: 13,
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    lineHeight: 1.7,
    outline: "none",
    resize: "vertical",
    boxSizing: "border-box",
  },
  historyList: { display: "flex", flexDirection: "column", gap: 12 },
  historyCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#111827",
    border: "1px solid #1e2535",
    borderRadius: 10,
    padding: "16px 20px",
  },
  historyLeft: { display: "flex", flexDirection: "column", gap: 4 },
  historyLabel: { fontSize: 14, fontWeight: 600, color: "#f1f5f9" },
  historyMeta: { fontSize: 12, color: "#64748b" },
  historyParams: { fontSize: 12, color: "#475569" },
  restoreBtn: {
    padding: "8px 16px",
    background: "#1e2535",
    border: "1px solid #2d3748",
    borderRadius: 8,
    color: "#a78bfa",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  loadingRow: { color: "#64748b", fontSize: 13, padding: "12px 0" },
  errorRow: { color: "#ef4444", fontSize: 13, padding: "12px 0" },
  emptyRow: { color: "#64748b", fontSize: 13, padding: "12px 0" },
};
